import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Archive, Download, Receipt } from "lucide-react";
import { api, download, errMsg } from "@/lib/api";
import { PageHeader, QuotaBar, Empty, Panel } from "@/components/Common";
import { LogoUploader } from "@/components/LogoUploader";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { StudioRateScadenza } from "@/components/studio/StudioRateScadenza";

export default function StudioCompanies() {
  const [d, setD] = useState(null);
  const { user, reload } = useAuth();
  useEffect(() => { api.get("/studio/overview").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-sm text-slate-500">Caricamento…</div>;

  const zip = (company_id) => download("/studio/export", { company_id }).catch((e) => toast.error(e.message));
  const manage = async (company_id) => {
    try { await api.post("/auth/billing-mode", { on: true, company_id }); window.location.href = "/app"; } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Aziende abbinate" subtitle={`${d.studio?.denominazione} · ${d.companies.length} aziende clienti`}>
        <Button asChild variant="outline" className="bg-white" data-testid="open-archive-button"><Link to="/studio/archivio"><Archive className="mr-2 h-4 w-4" />Archivio XML</Link></Button>
      </PageHeader>
      {isAdmin(user) && !user.demo && (
        <Panel title="Logo dello studio" className="mb-6">
          <LogoUploader endpoint="/studio/logo" value={d.studio?.logo} testid="studio-logo" hint="Appare nel menu dello studio. PNG o JPG, max 280 KB" onSaved={(logo) => { setD((x) => ({ ...x, studio: { ...x.studio, logo } })); reload(); }} />
        </Panel>
      )}
      <StudioRateScadenza />
      {d.companies.length === 0 ? <Empty text="Nessuna azienda abbinata a questo studio. Lo studio principale gestisce gli abbinamenti." /> : (
        <div className="stagger grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {d.companies.map((c) => (
            <div key={c.id} data-testid={`studio-company-${c.id}`} className="lift rounded-xl border border-slate-200 bg-white p-6">
              <div className="font-display text-base font-bold text-slate-900">{c.denominazione}</div>
              <div className="mt-0.5 font-mono text-xs text-slate-500">P.IVA {c.piva} · {c.comune}</div>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-slate-50 p-3"><div className="text-[11px] uppercase tracking-wide text-slate-500">XML emessi</div><div className="font-mono text-xl font-semibold">{c.count_out}</div></div>
                <div className="rounded-lg bg-slate-50 p-3"><div className="text-[11px] uppercase tracking-wide text-slate-500">XML ricevuti</div><div className="font-mono text-xl font-semibold">{c.count_in}</div></div>
              </div>
              <div className="mt-4"><QuotaBar used={c.xml_used} quota={c.xml_quota} /></div>
              <div className="mt-5 flex flex-wrap gap-2">
                {c.propria && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid={`studio-company-manage-${c.id}`} onClick={() => manage(c.id)}><Receipt className="mr-1.5 h-4 w-4" />Gestisci fatturazione</Button>}
                <Button asChild size="sm" variant="outline" data-testid={`studio-company-archive-${c.id}`}><Link to={`/studio/archivio?company=${c.id}`}>Documenti</Link></Button>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid={`studio-company-zip-${c.id}`} onClick={() => zip(c.id)}><Download className="mr-1.5 h-4 w-4" />ZIP XML</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
